import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../common/Button';
import { ErrorMessage } from '../common/ErrorMessage';

interface GroupModalProps {
  isOpen: boolean;
  mode: 'create' | 'rename';
  initialName?: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}

export const GroupModal: React.FC<GroupModalProps> = ({
  isOpen,
  mode,
  initialName = '',
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError('');
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    } else {
      setName('');
      setError('');
    }
  }, [isOpen, initialName]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();

    if (!trimmed) {
      setError('Group name is required.');
      return;
    }

    if (trimmed.length > 50) {
      setError('Group name must not exceed 50 characters.');
      return;
    }

    if (mode === 'rename' && trimmed.toLowerCase() === initialName.trim().toLowerCase()) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save group.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div
        className="relative w-full max-w-md glass-card border-white/10 p-6 rounded-2xl shadow-2xl bg-surface-950/95 border text-slate-100 animate-scale-up"
        role="dialog"
        aria-modal="true"
        aria-labelledby="group-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary-500/20 border border-primary-500/30 flex items-center justify-center text-primary-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <h2 id="group-modal-title" className="text-lg font-bold text-white">
                {mode === 'create' ? 'Create Vocabulary Group' : 'Rename Group'}
              </h2>
              <p className="text-xs text-slate-400">
                {mode === 'create'
                  ? 'Organize your words into a custom collection.'
                  : `Update the name for "${initialName}".`}
              </p>
            </div>
          </div>
          <button
            id="close-group-modal-btn"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error notification */}
        {error && (
          <div className="mt-4">
            <ErrorMessage message={error} />
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="group-name-input" className="text-sm font-medium text-slate-300">
                Group Name <span className="text-red-400">*</span>
              </label>
              <span className={`text-xs ${name.length > 45 ? 'text-amber-400' : 'text-slate-500'}`}>
                {name.length}/50
              </span>
            </div>
            <input
              ref={inputRef}
              id="group-name-input"
              type="text"
              placeholder="e.g. IELTS Environment, Technology, Week 1"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError('');
              }}
              maxLength={50}
              disabled={isSubmitting}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40 transition-all"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              id="submit-group-btn"
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={!name.trim() || isSubmitting}
            >
              {mode === 'create' ? 'Create Group' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
